/**
 * ScheduleContentContent
 *
 * Content component for the schedule content sheet.
 * Renders a date/time picker and 3 action buttons (Save as draft, Publish now, Schedule).
 * Matches Flutter's ScheduleContentScreen from schedule_content_screen.dart.
 */

import React, { useState, useMemo } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { CTAButtons } from '@/presentation/components/common/sheet/service/components/CTAButtons';
import { ValidateScheduleDateUseCase } from '@/domain/usecases/publish/validateScheduleDateUseCase';
import { DateUtils } from '@/presentation/utils/dateUtils';
import { DateTimePicker } from '@/presentation/components/common/controls/DateTimePicker';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';

export enum ScheduleAction {
  draft = 'draft',
  publishNow = 'publishNow',
  schedule = 'schedule',
}

export interface ScheduleContentSheetResult {
  action: ScheduleAction;
  timestamp?: number;
}

interface ScheduleContentContentProps {
  initialSchedule?: number | null;
  onResolve: (result: ScheduleContentSheetResult | null) => void;
}


export const ScheduleContentContent: React.FC<ScheduleContentContentProps> = ({
  initialSchedule,
  onResolve,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const { minDate, maxDate, initialDate } = useMemo(() => {
    const useCase = new ValidateScheduleDateUseCase();
    return useCase.getDateBounds(initialSchedule);
  }, [initialSchedule]);

  const [selectedDate, setSelectedDate] = useState<string>(DateUtils.toDatetimeLocalString(initialDate));

  const handleSaveAsDraft = () => {
    onResolve({ action: ScheduleAction.draft });
    SheetService.close();
  };

  const handlePublishNow = () => {
    onResolve({ action: ScheduleAction.publishNow });
    SheetService.close();
  };

  const handleSchedule = () => {
    const timestamp = new Date(selectedDate).getTime();
    onResolve({ action: ScheduleAction.schedule, timestamp });
    SheetService.close();
  };

  return (
    <div>
      {/* Date/time picker */}
      <div
        style={{
          paddingLeft: responsive.spacing['2xl'],
          paddingRight: responsive.spacing['2xl'],
          paddingTop: responsive.spacing['2xl'],
          paddingBottom: responsive.spacing.base,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <DateTimePicker
          value={selectedDate}
          min={DateUtils.toDatetimeLocalString(minDate)}
          max={DateUtils.toDatetimeLocalString(maxDate)}
          onChange={setSelectedDate}
          qa={{ id: 'schedule-content-picker' }}
        />
      </div>

      {/* Info text about scheduling constraints (e.g. "Content must be scheduled at
      least 3 hours in advance and no more than 30 days in advance") */}
      <div
        style={{
          paddingLeft: responsive.spacing['2xl'],
          paddingRight: responsive.spacing['2xl'],
          marginBottom: responsive.gap['5xl'],
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
        }}
      >
        {l10n.publish.scheduleContentInfo
          .replace('{minHours}', String(remoteConfigService.contentScheduleMinHours))
          .replace('{maxDays}', String(Math.floor(remoteConfigService.contentScheduleMaxHours / 24)))}
      </div>

      {/* CTA Buttons */}
      <CTAButtons
        cta={{
          orientation: 'column',
          buttons: [
            {
              text: l10n.publish.scheduleSaveAsDraft,
              onClick: handleSaveAsDraft,
              qaId: 'schedule-content-draft',
            },
            {
              text: l10n.publish.schedulePublishNowButton,
              onClick: handlePublishNow,
              qaId: 'schedule-content-publish-now',
            },
            {
              text: l10n.publish.scheduleButton,
              onClick: handleSchedule,
              textColor: theme.exclusive,
              qaId: 'schedule-content-confirm',
            },
          ],
        }}
        onClose={() => { }}
      />
    </div>
  );
};
